import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Footer } from '../components/landing/Footer';
import { ChevronDown, Menu } from 'lucide-react';
import { HeroContent } from '../components/landing/HeroContent';
import { SkewedLinesBackground } from '../components/backgrounds/SkewedLinesBackground';
import { useTranslation } from '../hooks/useTranslation';

const TermsOfServicePage: React.FC = () => {
  const [activeSection, setActiveSection] = useState<string | null>(null);
  const [hoveredLink, setHoveredLink] = useState<number | null>(null);
  const [tocOpen, setTocOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const parentRef = useRef<HTMLDivElement>(null);
  const sectionRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const { t } = useTranslation();

  // Register section refs
  useEffect(() => {
    // Force a reflow to ensure elements have proper dimensions
    setTimeout(() => {
      // Intentionally left empty - no logging needed
    }, 500);
  }, []);

  const toggleSection = (section: string) => {
    if (activeSection === section) {
      setActiveSection(null);
    } else {
      setActiveSection(section);
    }
  };

  // Custom scroll function
  const scrollToSection = (sectionId: string, closeMenu: boolean = false) => {
    // Get the section ref
    const sectionRef = sectionRefs.current[sectionId];
    
    if (sectionRef) {
      // Get the position of the section
      const rect = sectionRef.getBoundingClientRect();
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const sectionTop = rect.top + scrollTop;
      
      // Scroll to the section
      window.scrollTo({
        top: sectionTop - 100, // 100px offset from top
        behavior: 'smooth'
      });
      
      if (closeMenu) {
        setTocOpen(false);
      }
    }
  };

  const sections = [
    {
      id: 'acceptance',
      title: 'Acceptance of Terms',
      content: 'By accessing or using InvoiceHappens, you agree to be bound by these Terms of Service. If you do not agree to all the terms and conditions, you may not access or use our services.'
    },
    {
      id: 'description',
      title: 'Service Description',
      content: 'InvoiceHappens provides an invoicing platform designed for entrepreneurs. We offer tools to create, manage, and send invoices. We reserve the right to modify, suspend, or discontinue any part of our service at any time.'
    },
    {
      id: 'account',
      title: 'User Accounts',
      content: 'While basic invoice creation does not require an account, some features may require registration. You are responsible for maintaining the confidentiality of your account information and for all activities that occur under your account. You must notify us immediately of any unauthorized use of your account.'
    },
    {
      id: 'user-content',
      title: 'User Content',
      content: 'You retain all rights to the content you create using our service. By using InvoiceHappens, you grant us a non-exclusive license to use, store, and process your content solely for the purpose of providing and improving our services.'
    },
    {
      id: 'prohibited',
      title: 'Prohibited Activities',
      content: 'You agree not to use our service for any unlawful purpose or in any way that could damage, disable, or impair our service. Prohibited activities include but are not limited to: violating any applicable laws, infringing on intellectual property rights, transmitting malware, and attempting to gain unauthorized access to our systems.'
    },
    {
      id: 'intellectual-property',
      title: 'Intellectual Property',
      content: 'The InvoiceHappens service, including its original content, features, and functionality, is owned by us and is protected by international copyright, trademark, and other intellectual property laws. You may not copy, modify, distribute, or create derivative works based on our service without our express written permission.'
    },
    {
      id: 'disclaimer',
      title: 'Disclaimer of Warranties',
      content: 'Our service is provided "as is" and "as available" without any warranties of any kind, either express or implied. We do not guarantee that our service will be uninterrupted, secure, or error-free.'
    },
    {
      id: 'limitation',
      title: 'Limitation of Liability',
      content: 'To the fullest extent permitted by applicable law, we shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including but not limited to loss of profits, data, or use, arising out of or in connection with your use of our service.'
    },
    {
      id: 'changes-terms',
      title: 'Changes to Terms',
      content: 'We may modify these Terms of Service at any time. We will notify you of any changes by posting the new Terms on this page. Your continued use of the service after any such changes constitutes your acceptance of the new Terms.'
    },
    {
      id: 'governing-law',
      title: 'Governing Law',
      content: 'These Terms shall be governed by and construed in accordance with the laws of the Netherlands, without regard to its conflict of law provisions.'
    },
    {
      id: 'contact-terms',
      title: 'Contact Us',
      content: 'If you have any questions about these Terms, please contact us through the contact information provided on our website.'
    }
  ];

  return (
    <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900" ref={parentRef}>
      {/* Hero Section with Navigation */}
      <div className="relative h-[50vh] min-h-[400px] w-full overflow-hidden bg-white dark:bg-neutral-950" ref={containerRef}>
        {/* Skewed Lines Background */}
        <SkewedLinesBackground />
        
        {/* Navbar */}
        <HeroContent containerRef={containerRef} parentRef={parentRef} showHeroProps={false} />
        
        {/* Terms of Service Title */}
        <div className="absolute inset-0 z-10 flex items-center justify-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="text-center"
          >
            <motion.h1 
              className="text-4xl md:text-6xl font-bold text-center text-gray-900 dark:text-white"
            >
              Terms of Service
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="mt-4 max-w-2xl mx-auto text-center text-base text-neutral-800 dark:text-neutral-200"
            >
              Last updated: May 29, 2025
            </motion.p>
          </motion.div>
        </div>
      </div>

      {/* Content */}
      <div className="mx-auto flex w-full max-w-7xl flex-col gap-4 px-4 py-16 md:flex-row md:px-8">
        {/* Table of Contents - Desktop */}
        <div 
          className="sticky left-0 top-20 hidden max-w-xs flex-col self-start pr-10 md:flex"
          onMouseLeave={() => setHoveredLink(null)}
        >
          {sections.map((section, index) => (
            <a
              className="group/toc-link relative rounded-lg px-2 py-1 text-sm text-neutral-700 dark:text-neutral-200 cursor-pointer"
              key={section.id}
              href={`#${section.id}`}
              onClick={(e) => {
                e.preventDefault();
                scrollToSection(section.id);
              }}
              onMouseEnter={() => setHoveredLink(index)}
              onMouseLeave={() => setHoveredLink(null)}
            >
              {hoveredLink === index && (
                <motion.span
                  layoutId="toc-indicator"
                  className="absolute left-0 top-0 h-full w-1 rounded-br-full rounded-tr-full bg-neutral-200 dark:bg-neutral-700"
                />
              )}
              <span className="inline-block transition duration-200 group-hover/toc-link:translate-x-1">
                {section.title}
              </span>
            </a>
          ))}
        </div>

        {/* Mobile TOC Button */}
        <div className="sticky right-2 top-20 flex w-full flex-col items-end justify-end self-start md:hidden">
          <button
            onClick={() => setTocOpen(!tocOpen)}
            className="flex h-10 w-10 items-center justify-center rounded-full bg-white shadow-sm dark:bg-neutral-900"
          >
            <Menu className="h-6 w-6 text-black dark:text-white" />
          </button>
          <AnimatePresence>
            {tocOpen && (
              <motion.div
                initial={{
                  opacity: 0,
                }}
                animate={{
                  opacity: 1,
                }}
                exit={{
                  opacity: 0,
                }}
                transition={{
                  duration: 0.2,
                }}
                className="mt-2 flex flex-col items-end rounded-3xl border border-neutral-100 bg-white p-4 dark:border-neutral-700 dark:bg-neutral-900"
              >
                {sections.map((section, index) => (
                  <a
                    className="group/toc-link relative rounded-lg px-2 py-1 text-right text-sm text-neutral-700 dark:text-neutral-200 cursor-pointer"
                    key={section.id}
                    href={`#${section.id}`}
                    onClick={(e) => {
                      e.preventDefault();
                      scrollToSection(section.id, true);
                    }}
                    onMouseEnter={() => setHoveredLink(index)}
                    onMouseLeave={() => setHoveredLink(null)}
                  >
                    {hoveredLink === index && (
                      <motion.span
                        layoutId="toc-indicator-mobile"
                        className="absolute left-0 top-0 h-full w-1 rounded-br-full rounded-tr-full bg-neutral-200 dark:bg-neutral-700"
                      />
                    )}
                    <span className="inline-block transition duration-200 group-hover/toc-link:translate-x-1">
                      {section.title}
                    </span>
                  </a>
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Main Content */}
        <div className="flex max-w-2xl flex-1 flex-col">
          <div className="prose prose-neutral dark:prose-invert max-w-none">
            <div className="mb-6 p-3 bg-neutral-100 dark:bg-neutral-800 rounded-lg text-center">
              <p className="text-neutral-600 dark:text-neutral-300 text-sm font-medium">
                This content is available in English only
              </p>
            </div>
            <p className="text-neutral-600 dark:text-neutral-300">
              Welcome to InvoiceHappens. By using our service, you agree to these Terms of Service. Please read them carefully. These terms establish the rights, obligations, and limitations that apply to your use of our service. If you do not agree to these terms, please do not use our service.
            </p>

            {/* Mobile Accordion */}
            <div className="md:hidden mt-6 space-y-4">
              {sections.map((section) => (
                <div 
                  key={section.id} 
                  id={section.id}
                  ref={(el) => { sectionRefs.current[section.id] = el; }}
                  className="scroll-mt-24"
                >
                  <motion.div 
                    className="border border-neutral-200 dark:border-neutral-800 rounded-lg overflow-hidden"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <button
                      onClick={() => toggleSection(section.id)}
                      className="flex w-full items-center justify-between p-4 text-left font-medium text-neutral-900 dark:text-white"
                    >
                      <span>{section.title}</span>
                      <ChevronDown 
                        className={`h-5 w-5 transition-transform duration-300 ${activeSection === section.id ? 'rotate-180' : ''}`}
                      />
                    </button>
                    <AnimatePresence>
                      {activeSection === section.id && (
                        <motion.div 
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.3 }}
                          className="overflow-hidden"
                        >
                          <div className="p-4 pt-0 text-neutral-600 dark:text-neutral-300">
                            <p>{section.content}</p>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                </div>
              ))}
            </div>

            {/* Desktop Content */}
            <div className="hidden md:block space-y-8 mt-6">
              {sections.map((section, index) => (
                <div 
                  key={section.id} 
                  id={section.id} 
                  ref={(el) => { sectionRefs.current[section.id] = el; }}
                  className="scroll-mt-24"
                >
                  <motion.div 
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                  >
                    <h2 className="text-2xl font-bold text-neutral-900 dark:text-white mb-4">
                      {section.title}
                    </h2>
                    <p className="text-neutral-600 dark:text-neutral-300">
                      {section.content}
                    </p>
                  </motion.div>
                </div>
              ))}
            </div>
          </div>

          {/* Divider */}
          <div className="mt-10 max-w-2xl">
            <div className="h-px w-full bg-neutral-200 dark:bg-neutral-900" />
            <div className="h-px w-full bg-neutral-100 dark:bg-neutral-800" />
          </div>

          {/* Back to Top Button */}
          <motion.div 
            className="mt-10 text-center"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <button 
              onClick={() => {
                try {
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                } catch {
                  // Silent error handling
                }
              }}
              className="inline-flex items-center px-4 py-2 rounded-full bg-neutral-100 hover:bg-neutral-200 dark:bg-neutral-800 dark:hover:bg-neutral-700 text-sm font-medium text-neutral-600 hover:text-neutral-900 dark:text-neutral-400 dark:hover:text-white transition-all duration-300"
            >
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                className="h-4 w-4 mr-2" 
                fill="none" 
                viewBox="0 0 24 24" 
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
              </svg>
              {t('footer.backToTop')}
            </button>
          </motion.div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default TermsOfServicePage; 